'use client';

import type { ProfileModuleDto } from '@up2stay/up2promo-api-client';
import { ProfileLayout, ProfileTheme } from '@up2stay/up2promo-api-client';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '../components/button';
import { Card, CardContent } from '../components/card';
import { Skeleton } from '../components/skeleton';
import { cn } from '../utils/cn';
import {
  hasAutoOpenedLeadForm,
  hasDismissedLeadFormRecently,
  hasSubmittedLeadForm,
  markLeadFormAutoOpened,
  markLeadFormDismissed,
} from './lib/lead-form-storage';
import { ProfileCtaButtons } from './layouts/profile-cta-buttons';
import { ProfileHeader } from './layouts/profile-header';
import { ProfileLayoutProvider } from './layouts/profile-layout-context';
import {
  ProfileModuleShell,
  ProfileSectionLabel,
} from './layouts/profile-module-shell';
import { getModuleLabel, getModuleSectionTitle } from './module-labels';
import { hasModuleContent } from './modules/module-has-content';
import { PublicModuleRenderer } from './modules/public-module-renderer';
import { ProfileThemeRoot } from './profile-accent-theme';
import { PublicLeadForm } from './public-lead-form';
import { PublicProfileBranding } from './public-profile-branding';
import {
  resolvePublicProfileHeaderStyle,
  resolvePublicProfileLayout,
  type PublicProfileStyling,
} from './public-profile-layout';
import { PublicProfileLegalFooter } from './public-profile-legal-footer';
import {
  resolvePublicProfileModuleChrome,
  resolvePublicProfileModuleTypeLabels,
} from './public-profile-module-copy';
import { resolveSaveContactBlockedCopy } from './save-contact-in-app-guide';

export type Dictionary = {
  publicProfile?: {
    userFallback?: string;
    profileNotFound?: string;
    profileNotFoundDescription?: string;
    visitHome?: string;
    username?: string;
    workspace?: string;
    legal?: {
      impressum?: string;
      privacy?: string;
      terms?: string;
      navAria?: string;
    };
    branding?: {
      label?: string;
      linkAria?: string;
      tagline?: string;
    };
    saveContact?: string;
    saveContactBlocked?: {
      title?: string;
      description?: string;
      genericApp?: string;
      iosStepMenu?: string;
      iosStepBrowser?: string;
      iosStepSave?: string;
      openInBrowser?: string;
      copyLink?: string;
      copied?: string;
      copyManually?: string;
      linkLabel?: string;
    };
    modules?: Record<string, unknown>;
    [key: string]: unknown;
  };
  leads?: {
    form?: {
      title?: string;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

type PublicProfile = {
  id?: string;
  title?: string | null;
  jobTitle?: string | null;
  company?: string | null;
  location?: string | null;
  profileImage?: { path: string } | null;
  showProfileImage?: boolean;
  showUp2promoBranding?: boolean;
  ownerPhoto?: { path: string } | null;
  organizationPhoto?: { path: string } | null;
  modules?: unknown[];
  styling?: PublicProfileStyling | null;
  leadForm?: {
    enabled: boolean;
    visibilityMode?: 'off' | 'manual' | 'auto_open';
    autoOpenDelay?: number;
    fields?: Array<{
      id?: string;
      type: string;
      label: string;
      required: boolean;
      order: number;
      config?: { key?: string; options?: string[] };
    }>;
  };
};

function publicProfileContactVcfUrl(
  apiOrigin: string | null | undefined,
  profileId: string | undefined,
): string | null {
  if (!apiOrigin?.trim() || !profileId) return null;
  return `${apiOrigin.replace(/\/$/, '')}/api/v1/profiles-public/id/${encodeURIComponent(profileId)}/contact.vcf`;
}

interface PublicProfileContentProps {
  profile: PublicProfile | null | undefined;
  isLoading: boolean;
  error: Error | null;
  username: string;
  workspaceId?: string | null;
  workspaceUsername?: string | null;
  isPreview?: boolean;
  showUp2promoBranding?: boolean;
  dictionary?: Dictionary;
  googleMapsApiKey?: string | null;
  profileBaseUrl?: string;
  fileBaseUrl?: string | null;
  getFileUrl?: (path: string) => string;
  onContact?: () => void;
  contactLabel?: string;
  embeddedInApp?: boolean;
}

/** Modules whose entries already look like cards — no outer card on cards-mobile. */
const CARDS_ENTRY_MODULE_TYPES = new Set([
  'contact_info',
  'links',
  'files',
  'socials',
  'images',
  'team_members',
]);

/** Media modules: outer card, but no body padding (flush media). */
const CARDS_FLUSH_SHELL_TYPES = new Set(['video', 'google_maps']);

const CLASSIC_SIDEBAR_TYPES = new Set(['contact_info', 'socials', 'files']);

const CARDS_FULL_WIDTH_TYPES = new Set(['images', 'google_maps']);

export function PublicProfileContent({
  profile,
  isLoading,
  error,
  username,
  workspaceUsername,
  isPreview = false,
  showUp2promoBranding: showUp2promoBrandingProp,
  dictionary,
  googleMapsApiKey,
  profileBaseUrl = '',
  fileBaseUrl,
  getFileUrl,
  onContact,
  contactLabel,
  embeddedInApp = false,
}: PublicProfileContentProps & { dictionary?: Dictionary }) {
  const dict = dictionary || { publicProfile: {} };
  const moduleChrome = resolvePublicProfileModuleChrome(
    dict.publicProfile?.modules,
  );
  const moduleTypeLabels = resolvePublicProfileModuleTypeLabels(
    dict.publicProfile?.modules,
  );
  const resolveFileUrl =
    getFileUrl ??
    (fileBaseUrl
      ? (path: string) => {
          if (path.startsWith('data:') || path.startsWith('blob:')) {
            return path;
          }
          const base = fileBaseUrl.replace(/\/$/, '');
          if (path.startsWith('http://') || path.startsWith('https://')) {
            try {
              const u = new URL(path);
              const isPrivateOrLocal =
                u.hostname === 'localhost' ||
                u.hostname === '127.0.0.1' ||
                u.hostname.startsWith('192.168.') ||
                u.hostname.startsWith('10.') ||
                /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(u.hostname);
              if (isPrivateOrLocal) {
                return base + (u.pathname || '') + (u.search || '');
              }
            } catch {
              // ignore
            }
            return path;
          }
          return base + (path.startsWith('/') ? path : `/${path}`);
        }
      : (path: string) => path);

  const [isLeadFormOpen, setIsLeadFormOpen] = useState(false);
  const [inlineCtaVisible, setInlineCtaVisible] = useState(true);
  const [pageFooterVisible, setPageFooterVisible] = useState(false);
  const ctaButtonRef = useRef<HTMLButtonElement>(null);
  const pageFooterRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const layout = resolvePublicProfileLayout(profile?.styling?.layout);
  const headerStyle = resolvePublicProfileHeaderStyle({
    headerStyle: profile?.styling?.headerStyle,
    headerImagePath: profile?.styling?.headerImage?.path,
  });
  // Classic screens default to light; cards/centered to dark when theme unset.
  const themeMode =
    profile?.styling?.theme ??
    (layout === ProfileLayout.CLASSIC ? ProfileTheme.LIGHT : ProfileTheme.DARK);
  const palette = layout === ProfileLayout.CLASSIC ? 'classic' : 'standard';

  const showExchangeContactCta = Boolean(onContact && contactLabel);
  const contactVcfUrl = profile?.id
    ? publicProfileContactVcfUrl(fileBaseUrl, profile.id)
    : null;
  const saveContactLabel = dict.publicProfile?.saveContact || 'Save contact';
  const hasFloatingLeadCta =
    !isPreview &&
    Boolean(profile?.leadForm?.enabled) &&
    (profile?.leadForm?.visibilityMode === 'manual' ||
      profile?.leadForm?.visibilityMode === 'auto_open') &&
    (!onContact || showExchangeContactCta);
  const openContact = onContact ?? (() => setIsLeadFormOpen(true));
  const showFloatingCta =
    hasFloatingLeadCta && !inlineCtaVisible && !pageFooterVisible;

  useEffect((): void | (() => void) => {
    if (onContact) return;
    if (isPreview) return;
    if (
      !profile?.id ||
      !profile.leadForm?.enabled ||
      profile.leadForm.visibilityMode !== 'auto_open'
    ) {
      return;
    }
    const profileId = profile.id;
    if (
      hasSubmittedLeadForm(profileId) ||
      hasAutoOpenedLeadForm(profileId) ||
      hasDismissedLeadFormRecently(profileId)
    ) {
      return;
    }
    const delayMs = (profile.leadForm.autoOpenDelay ?? 0) * 1000;
    const timeoutId = setTimeout(() => {
      markLeadFormAutoOpened(profileId);
      setIsLeadFormOpen(true);
    }, delayMs);
    return () => clearTimeout(timeoutId);
  }, [profile?.leadForm, profile?.id, isPreview, onContact]);

  const displayName = useMemo(() => {
    if (profile?.title) return profile.title;
    return dict.publicProfile?.userFallback || 'User';
  }, [profile, dict.publicProfile?.userFallback]);

  const enabledModules = useMemo(() => {
    if (!profile?.modules || !Array.isArray(profile.modules)) return [];
    return (profile.modules as ProfileModuleDto[])
      .filter((module) => module.enabled === true && hasModuleContent(module))
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [profile]);

  useEffect(() => {
    const ctaButton = ctaButtonRef.current;
    const pageFooter = pageFooterRef.current;
    const scrollContainer = scrollContainerRef.current;
    if (!ctaButton || isPreview || !hasFloatingLeadCta) return;

    const root =
      scrollContainer &&
      scrollContainer.scrollHeight > scrollContainer.clientHeight
        ? scrollContainer
        : null;

    const ctaObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => setInlineCtaVisible(entry.isIntersecting));
      },
      { root, rootMargin: '0px', threshold: 0 },
    );
    ctaObserver.observe(ctaButton);

    let footerObserver: IntersectionObserver | null = null;
    if (pageFooter) {
      footerObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) =>
            setPageFooterVisible(entry.isIntersecting),
          );
        },
        { root, rootMargin: '0px 0px -112px 0px', threshold: 0 },
      );
      footerObserver.observe(pageFooter);
    }

    const handleScrollOrResize = () => {
      if (!ctaButton || root) return;
      const rect = ctaButton.getBoundingClientRect();
      setInlineCtaVisible(
        rect.top < window.innerHeight &&
          rect.bottom > 0 &&
          rect.left < window.innerWidth &&
          rect.right > 0,
      );
      if (pageFooter) {
        const footerRect = pageFooter.getBoundingClientRect();
        setPageFooterVisible(
          footerRect.top < window.innerHeight - 112 && footerRect.bottom > 0,
        );
      }
    };

    window.addEventListener('scroll', handleScrollOrResize, { passive: true });
    window.addEventListener('resize', handleScrollOrResize);
    const t = setTimeout(handleScrollOrResize, 100);

    return () => {
      clearTimeout(t);
      ctaObserver.disconnect();
      footerObserver?.disconnect();
      window.removeEventListener('scroll', handleScrollOrResize);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [hasFloatingLeadCta, isPreview]);

  const shellMinClass = isPreview
    ? 'min-h-full'
    : embeddedInApp
      ? 'min-h-[50vh]'
      : 'min-h-screen';

  if (isLoading) {
    return (
      <div
        className={cn(
          'flex w-full items-center justify-center bg-[var(--bg,#0a0c0f)] p-4',
          shellMinClass,
        )}
      >
        <Card className="w-full max-w-md">
          <CardContent className="space-y-6 p-8">
            <Skeleton className="mx-auto h-24 w-24 rounded-full" />
            <Skeleton className="mx-auto h-8 w-48" />
            <Skeleton className="h-20 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div
        className={cn(
          'flex w-full items-center justify-center bg-[var(--bg,#0a0c0f)] p-4',
          shellMinClass,
        )}
      >
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <h1 className="mb-2 text-2xl font-bold">
              {dict.publicProfile?.profileNotFound || 'Profile Not Found'}
            </h1>
            <p className="mb-4 text-muted-foreground">
              {dict.publicProfile?.profileNotFoundDescription ||
                "This profile doesn't exist or is not publicly accessible."}
            </p>
            {username ? (
              <p className="mb-6 text-sm text-muted-foreground">
                {workspaceUsername ? `${workspaceUsername}/` : ''}
                {username}
              </p>
            ) : null}
            <Button asChild className="text-white">
              <a href="https://www.up2promo.com">
                {dict.publicProfile?.visitHome || 'up2promo.com'}
              </a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const showExchange =
    Boolean(profile.leadForm?.enabled) &&
    (!onContact || showExchangeContactCta) &&
    (profile.leadForm?.visibilityMode === 'manual' ||
      profile.leadForm?.visibilityMode === 'auto_open');

  const exchangeLabel = contactLabel || dict.leads?.form?.title || 'Contact';

  // Cards/classic: CTAs live in the header from first paint (CSS places them).
  // Never gate this on matchMedia — that caused the desktop layout jump.
  // Preview uses the same embedding so the phone chrome matches live.
  const ctaInHeader =
    layout === ProfileLayout.CARDS || layout === ProfileLayout.CLASSIC;

  const cta = (
    <ProfileCtaButtons
      layout={layout}
      buttonShape={profile.styling?.buttonShape}
      showExchange={showExchange}
      exchangeLabel={exchangeLabel}
      onExchange={openContact}
      exchangeRef={ctaButtonRef}
      saveContactUrl={contactVcfUrl}
      saveContactLabel={saveContactLabel}
      saveContactBlocked={resolveSaveContactBlockedCopy(
        dict.publicProfile?.saveContactBlocked,
      )}
      accentColor={profile.styling?.accentColor ?? null}
      theme={themeMode}
      palette={palette}
      embedded={ctaInHeader}
    />
  );

  const headerImageUrl = profile.styling?.headerImage?.path
    ? resolveFileUrl(profile.styling.headerImage.path)
    : null;

  const photoPath = (
    profile.profileImage?.path ?? profile.ownerPhoto?.path
  )?.trim();

  const renderModule = (module: ProfileModuleDto, index: number) => {
    const title = getModuleSectionTitle(
      module,
      getModuleLabel(module.type ?? '', moduleTypeLabels),
    );
    const type = module.type ?? '';
    const key = module.id || `${type}-${module.order}-${index}`;
    const body = (
      <PublicModuleRenderer
        module={module}
        googleMapsApiKey={googleMapsApiKey}
        workspaceUsername={workspaceUsername}
        profileBaseUrl={profileBaseUrl}
        currentUsername={username}
        getFileUrl={resolveFileUrl}
        chrome={moduleChrome}
      />
    );

    if (layout === ProfileLayout.CARDS) {
      const isEntry = CARDS_ENTRY_MODULE_TYPES.has(type);
      const flush = CARDS_FLUSH_SHELL_TYPES.has(type);
      // Mobile: section label outside; shell chrome dissolves for entry types.
      // Desktop: title inside card chrome (.chead), except flush media.
      // All via CSS — no post-hydration restructure.
      return (
        <section key={key} className="w-full min-w-0">
          {isEntry ? (
            <ProfileSectionLabel
              layout={layout}
              className="@min-[880px]/profile:hidden"
            >
              {title}
            </ProfileSectionLabel>
          ) : null}
          <ProfileModuleShell
            layout={layout}
            title={title}
            hideTitle={flush}
            flush={flush}
            fullWidth={CARDS_FULL_WIDTH_TYPES.has(type)}
            unframedOnMobile={isEntry}
          >
            {body}
          </ProfileModuleShell>
        </section>
      );
    }

    return (
      <ProfileModuleShell
        key={key}
        layout={layout}
        title={title}
        // Centered socials are an unlabeled icon row (handover), not a labeled section.
        hideTitle={layout === ProfileLayout.CENTERED && type === 'socials'}
      >
        {body}
      </ProfileModuleShell>
    );
  };

  const modulesBlock = (() => {
    if (enabledModules.length === 0) return null;

    // Cards: one CSS grid from first paint. Full-width modules span both cols
    // on desktop; source order matches mobile (no JS column flip).
    // Phone preview uses the same tree — @container/profile keeps it single-col.
    if (layout === ProfileLayout.CARDS) {
      return (
        <div className="mt-5 grid w-full min-w-0 grid-cols-1 items-start gap-5 @min-[880px]/profile:grid-cols-2">
          {enabledModules.map((m, i) => {
            const type = m.type ?? '';
            const key = m.id || `${type}-${m.order}-${i}`;
            return (
              <div
                key={key}
                className={cn(
                  'min-w-0',
                  CARDS_FULL_WIDTH_TYPES.has(type) &&
                    '@min-[880px]/profile:col-span-2',
                )}
              >
                {renderModule(m, i)}
              </div>
            );
          })}
        </div>
      );
    }

    // Classic: sidebar + main as independent columns on desktop; on mobile
    // `display:contents` + order restores module `order` in a single stack.
    // Desktop left column sticks while the longer right column scrolls.
    // Preview shares this tree; container queries keep the phone single-column.
    if (layout === ProfileLayout.CLASSIC) {
      const sidebar = enabledModules.filter((m) =>
        CLASSIC_SIDEBAR_TYPES.has(m.type ?? ''),
      );
      const main = enabledModules.filter(
        (m) => !CLASSIC_SIDEBAR_TYPES.has(m.type ?? ''),
      );
      return (
        <div
          className={cn(
            'mt-[34px] flex w-full min-w-0 flex-col gap-[34px]',
            '@min-[880px]/profile:mt-11 @min-[880px]/profile:grid @min-[880px]/profile:grid-cols-[minmax(0,320px)_minmax(0,1fr)] @min-[880px]/profile:items-start @min-[880px]/profile:gap-12',
          )}
        >
          <div
            className={cn(
              'flex min-w-0 flex-col gap-[34px] @max-[879px]/profile:contents',
              // Stick under viewport top (no fixed chrome on public profile).
              // max-h + overflow only kicks in if the sidebar itself exceeds the viewport.
              '@min-[880px]/profile:sticky @min-[880px]/profile:top-4 @min-[880px]/profile:max-h-[calc(100vh-2rem)] @min-[880px]/profile:overflow-y-auto',
            )}
          >
            {sidebar.map((m, i) => {
              const type = m.type ?? '';
              const key = m.id || `${type}-${m.order}-${i}`;
              return (
                <div
                  key={key}
                  className="min-w-0"
                  style={{ order: m.order ?? i }}
                >
                  {renderModule(m, i)}
                </div>
              );
            })}
          </div>
          <div className="flex min-w-0 flex-col gap-[34px] @max-[879px]/profile:contents">
            {main.map((m, i) => {
              const type = m.type ?? '';
              const key = m.id || `${type}-${m.order}-${i + sidebar.length}`;
              return (
                <div
                  key={key}
                  className="min-w-0"
                  style={{ order: m.order ?? i + sidebar.length }}
                >
                  {renderModule(m, i + sidebar.length)}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    // Centered: single stack; section rhythm comes from ProfileSectionLabel margins.
    return (
      <div className="flex w-full flex-col">
        {enabledModules.map((m, i) => renderModule(m, i))}
      </div>
    );
  })();

  const showBranding =
    (showUp2promoBrandingProp ?? profile.showUp2promoBranding ?? true) !==
    false;

  const wrapClass =
    layout === ProfileLayout.CENTERED
      ? cn(
          // Handover centered-mobile: 60/24/40. Keep 24px side padding at all
          // widths (desktop column is max 468px) — do not drop to px-1.
          'relative mx-auto w-full max-w-[468px] px-6 pb-10 pt-[60px] text-center @min-[880px]/profile:pb-11 @min-[880px]/profile:pt-14',
          // Iphone15Pro island bottom ≈ 48px into the screen — clear + small gap.
          isPreview && 'pt-14',
        )
      : layout === ProfileLayout.CLASSIC
        ? cn(
            'mx-auto w-full max-w-[1040px] px-[26px] pb-10 pt-[54px] @min-[880px]/profile:px-8 @min-[880px]/profile:pt-12',
            isPreview && 'px-4 pt-14',
          )
        : cn(
            'mx-auto w-full max-w-[960px] px-5 pb-10 pt-[52px] @min-[880px]/profile:px-6 @min-[880px]/profile:pt-11',
            isPreview && 'px-4 pt-14',
          );

  const headerAndCta = (
    <>
      <ProfileHeader
        layout={layout}
        headerStyle={headerStyle}
        headerImageUrl={headerImageUrl}
        identity={{
          displayName,
          jobTitle: profile.jobTitle,
          company: profile.company,
          location: profile.location,
          showProfileImage: profile.showProfileImage,
          photoPath,
        }}
        resolveFileUrl={resolveFileUrl}
        actions={ctaInHeader ? cta : undefined}
      />
      {!ctaInHeader ? cta : null}
    </>
  );

  const mainColumn = (
    <div className={cn(wrapClass, 'relative min-w-0')}>
      {layout === ProfileLayout.CENTERED ? (
        <div
          className="pointer-events-none absolute inset-x-0 top-0 -z-0 hidden h-[420px] @min-[880px]/profile:block"
          style={{
            background:
              'radial-gradient(60% 100% at 50% 0%, var(--accent-12), rgba(0,0,0,0) 70%)',
          }}
          aria-hidden
        />
      ) : null}
      <div className="relative z-[1] w-full min-w-0">
        {headerAndCta}
        {modulesBlock}
      </div>
    </div>
  );

  const leadFormBlock =
    profile.leadForm?.enabled && profile.id && !onContact ? (
      <PublicLeadForm
        dictionary={dict}
        profileId={profile.id}
        open={isLeadFormOpen}
        onOpenChange={(open) => {
          setIsLeadFormOpen(open);
          if (!open && profile.id) markLeadFormDismissed(profile.id);
        }}
        isPreview={isPreview}
        accentColor={profile.styling?.accentColor ?? null}
        theme={themeMode}
        palette={palette}
      />
    ) : null;

  const floatingCtaBlock = hasFloatingLeadCta ? (
    <div
      className={cn(
        'fixed left-0 right-0 z-[60] px-4',
        'bottom-[max(1rem,calc(1rem+env(safe-area-inset-bottom)))]',
        'pointer-events-none transition-all duration-300 ease-out',
        showFloatingCta
          ? 'translate-y-0 opacity-100'
          : 'translate-y-full opacity-0',
      )}
    >
      <div className="pointer-events-auto mx-auto w-full max-w-[960px]">
        <button
          type="button"
          onClick={openContact}
          className="flex h-14 w-full cursor-pointer items-center justify-center gap-2 rounded-[15px] bg-[image:var(--accent-grad)] text-base font-semibold text-[var(--accent-text)] shadow-[0_10px_26px_var(--accent-16)] transition-transform duration-150 hover:bg-[image:var(--accent-grad-hover)] active:scale-[0.99] active:bg-[image:var(--accent-grad-hover)]"
        >
          {exchangeLabel}
        </button>
      </div>
    </div>
  ) : null;

  const pageFooter = (
    <div ref={pageFooterRef} className="w-full shrink-0 px-5 pb-8">
      {showBranding ? (
        <PublicProfileBranding
          dictionary={dict}
          compact={isPreview}
          className={isPreview ? 'mt-auto' : undefined}
        />
      ) : null}
      <PublicProfileLegalFooter
        dictionary={dict}
        position="inline"
        className="!text-[var(--faint)] [&_a]:text-[var(--faint)] [&_a]:no-underline hover:[&_a]:text-[var(--muted)]"
      />
    </div>
  );

  const scrollContainerClassName = cn(
    'flex w-full flex-col',
    isPreview
      ? 'h-full flex-1 min-h-0 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'
      : embeddedInApp
        ? 'min-h-full'
        : 'min-h-screen',
    isPreview && isLeadFormOpen && 'overflow-hidden',
    hasFloatingLeadCta &&
      showFloatingCta &&
      'pb-[max(7rem,calc(7rem+env(safe-area-inset-bottom)))]',
  );

  const themeRoot = (
    <ProfileThemeRoot
      accentColor={profile.styling?.accentColor ?? null}
      theme={themeMode}
      palette={palette}
      className={cn(
        'relative w-full',
        isPreview ? 'flex h-full flex-col overflow-hidden' : '',
        embeddedInApp ? 'min-h-full' : isPreview ? '' : 'min-h-screen',
      )}
    >
      <ProfileLayoutProvider layout={layout}>
        <div
          ref={scrollContainerRef}
          className={scrollContainerClassName}
          style={
            isPreview
              ? {
                  height: '100%',
                  minHeight: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }
              : undefined
          }
        >
          {mainColumn}
          {pageFooter}
        </div>
        {leadFormBlock}
        {!isPreview ? floatingCtaBlock : null}
      </ProfileLayoutProvider>
    </ProfileThemeRoot>
  );

  return themeRoot;
}
